import type { UserPublic, ConnectedAccount, ManagedAccount, AccountPlatform } from './types'

/**
 * Extracts accounts from the user object as a fallback when
 * the /accounts endpoint is unavailable.
 */
export function normalizeAccounts(user: UserPublic): ConnectedAccount[] {
  return user.accounts ?? []
}

export function partitionManagedAccounts(accounts: ManagedAccount[]) {
  return {
    connected: accounts.filter((account) => account.connection_status === 'connected'),
    reconnectRequired: accounts.filter((account) => account.connection_status === 'reconnect_required'),
  }
}

export function promoteConnectedAccountsForManagement(accounts: ConnectedAccount[]): ManagedAccount[] {
  return accounts.map((account) => ({
    ...account,
    connection_status: 'connected',
    disconnected_at: null,
    has_retained_voice_profile: account.voice_config.voice_profile != null,
    reconnect_supported: account.platform === 'twitter',
  }))
}

export function reconcileManagedAccountsFallback(
  current: ManagedAccount[],
  operationalAccounts: ConnectedAccount[]
): ManagedAccount[] {
  const connected = promoteConnectedAccountsForManagement(operationalAccounts)
  const connectedIds = new Set(connected.map((account) => account.id))
  const retainedDisconnected = current.filter((account) =>
    account.connection_status === 'reconnect_required' && !connectedIds.has(account.id))

  return [...connected, ...retainedDisconnected]
}

/**
 * State of an in-flight OAuth connect/reconnect attempt. Pending state must
 * never survive leaving the page: it is cleared right before auth navigation
 * and reset on every pageshow (including BFCache returns), so an abandoned
 * OAuth flow can't leave a stuck spinner behind.
 */
export type ConnectionAttempt =
  | { status: 'idle' }
  | { status: 'pending'; kind: 'connect'; platform: AccountPlatform }
  | { status: 'pending'; kind: 'reconnect'; accountId: number }

export type ConnectionAttemptEvent =
  | { type: 'start-connect'; platform: AccountPlatform }
  | { type: 'start-reconnect'; accountId: number }
  | { type: 'auth-navigation' }
  | { type: 'page-show' }
  | { type: 'failed' }
  | { type: 'cancel' }

export const IDLE_CONNECTION_ATTEMPT: ConnectionAttempt = { status: 'idle' }

export function connectionAttemptReducer(
  state: ConnectionAttempt,
  event: ConnectionAttemptEvent
): ConnectionAttempt {
  switch (event.type) {
    case 'start-connect':
      return { status: 'pending', kind: 'connect', platform: event.platform }
    case 'start-reconnect':
      return { status: 'pending', kind: 'reconnect', accountId: event.accountId }
    case 'auth-navigation':
    case 'page-show':
    case 'failed':
    case 'cancel':
      return IDLE_CONNECTION_ATTEMPT
    default:
      return state
  }
}

export function canRemoveAccountPermanently(account: ManagedAccount): boolean {
  return account.connection_status === 'reconnect_required'
}

export function removeManagedAccountLocally(
  accounts: ManagedAccount[],
  accountId: number
): ManagedAccount[] {
  return accounts.filter((account) => account.id !== accountId)
}

export function getAccountInitial(account: { display_name?: string; username: string }): string {
  const source = account.display_name?.trim() || account.username.trim()
  return source ? source.charAt(0).toUpperCase() : '?'
}
